// Real-or-null selectors for the Executive Brain dashboard. RULE: a selector returns
// REAL data or null/[] — it NEVER fabricates. Placeholders live only in mock/mockData.js
// and are applied by the UI as a fallback, never here.
import { collectRealMoments } from "./voice.js";

// Greeting from /auth/me + the wall clock (both real).
export function selectGreeting(user, now = new Date()) {
  const h = (now instanceof Date ? now : new Date(now)).getHours();
  const part = h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  const name = (user && (user.name || user.operator_code)) || null;
  const first = name ? String(name).split(" ")[0] : null;
  return { part, name: first, text: first ? `${part}, ${first}` : part };
}

// Top-3 decision cards — real fields only (title/rank/confidence.tier/impact + status).
// Fields the queue does NOT carry (confidence %, ETA, risk) are intentionally absent;
// the UI fills those from mockData.
export function selectDecisionCards(queueData, limit = 3) {
  const items = (queueData && queueData.items) || [];
  return items.slice(0, limit).map((it) => ({
    item_id: it.item_id,
    rank: it.rank,
    title: it.title,
    item_type: it.item_type,
    business_unit: it.business_unit,
    confidenceTier: (it.confidence && it.confidence.tier) || null,
    dollarsPerDay: it.impact && it.impact.basis === "dollar" ? it.impact.dollars_per_day : null,
    basis: (it.impact && it.impact.basis) || null,
    status: it.status || (it.critical_override && it.critical_override.active ? "CRITICAL" : "PENDING"),
    action: it.action || null,
  }));
}

function count(v) {
  return typeof v === "number" && !Number.isNaN(v) ? v : null;
}

// Cluster counts — real-or-null per cluster (UI falls back to MOCK_CLUSTER_COUNTS only
// when a cluster's endpoint is disabled/absent).
export function selectClusterCounts({ predictionsSummary, recommendationsSummary, observations, experiments, journalSummary, lessonsSummary } = {}) {
  const obs = Array.isArray(observations) ? observations.length : count(observations && observations.total);
  const exp = Array.isArray(experiments) ? experiments.length : count(experiments && experiments.total);
  return {
    PREDICTION: count(predictionsSummary && predictionsSummary.total_predictions),
    RECOMMENDATION: count(recommendationsSummary && recommendationsSummary.total),
    OBSERVATION: obs,
    EXPERIMENT: exp,
    KNOWLEDGE: count(lessonsSummary && lessonsSummary.total), // the REAL lesson count (not 2846)
    MEMORY: count(journalSummary && journalSummary.total),
  };
}

// Momentum tallies that ARE real (counts). % deltas are placeholder (mockData).
export function selectMomentumCounts({ journalSummary, experiments, lessonsSummary } = {}) {
  const exp = Array.isArray(experiments) ? experiments.length : count(experiments && experiments.total);
  return {
    decisions: count(journalSummary && journalSummary.total),
    experiments: exp,
    knowledge: count(lessonsSummary && lessonsSummary.total),
  };
}

// Latest Thoughts — prefer REAL moments + recent observations; returns [] when nothing
// real exists (UI then falls back to MOCK_LATEST_THOUGHTS).
export function selectLatestThoughts({ outcomes = [], evaluations = [], observations = [] } = {}, { max = 5 } = {}) {
  const out = [];
  const moments = collectRealMoments({ outcomes, evaluations, journalEvents: [] });
  for (const s of moments.supersedes) out.push({ kind: "supersede", text: `Let go of my earlier read on ${s.subject}.` });
  for (const c of moments.confidenceDeltas) out.push({ kind: "confidence", text: `${c.direction === "increased" ? "Raised" : "Lowered"} confidence in ${c.subject}.` });
  for (const cf of moments.conflicts) out.push({ kind: "conflict", text: `Conflicting signals in ${cf.cohort_key || "one cohort"}.` });
  for (const o of observations.slice(0, max)) out.push({ kind: "observation", text: o.title || o.summary || o.text || "New observation." });
  return out.slice(0, max);
}
